"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  currentIndustryPresetIntentHash,
  industryPresetSlugFromHash,
} from "@/app/lib/industryPresetIntent";
import { useWorkspacePresetsQuery } from "../hooks/useWorkspacePresetsQuery";
import { isConfigurablePresetCode } from "../presetConfigurationRegistry";
import { getPresetCodeForPublicIndustrySlug } from "../presetIndustryPresentation";
import { PresetApplyModal } from "./PresetApplyModal";
import { hasIndustrySetup } from "../onboarding/registry";

export function IndustryPresetIntent({
  workspaceName,
  workspaceUuid,
}: {
  workspaceName: string;
  workspaceUuid: string;
}) {
  const [industrySlug, setIndustrySlug] = useState<string | null>(null);
  const presetCode = useMemo(
    () => industrySlug === null ? undefined : getPresetCodeForPublicIndustrySlug(industrySlug),
    [industrySlug],
  );
  const presetsQuery = useWorkspacePresetsQuery(workspaceUuid, presetCode !== undefined);
  const preset = presetsQuery.data?.presets.find((item) => item.code === presetCode);

  const clearIntent = useCallback(() => {
    if (typeof window !== "undefined") {
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${window.location.search}`,
      );
    }

    setIndustrySlug(null);
  }, []);

  useEffect(() => {
    function readIntent() {
      setIndustrySlug(industryPresetSlugFromHash(currentIndustryPresetIntentHash()));
    }

    readIntent();
    window.addEventListener("hashchange", readIntent);

    return () => window.removeEventListener("hashchange", readIntent);
  }, []);

  useEffect(() => {
    if (presetCode !== undefined && presetsQuery.isSuccess && preset === undefined) {
      clearIntent();
    }
  }, [clearIntent, preset, presetCode, presetsQuery.isSuccess]);

  if (
    preset === undefined
    || hasIndustrySetup(preset.code)
    || preset.readiness !== "production"
    || !isConfigurablePresetCode(preset.code)
  ) {
    return null;
  }

  return (
    <PresetApplyModal
      key={preset.code}
      initialCompanyName={workspaceName}
      marketingIntentWorkspaceName={workspaceName}
      open
      preset={preset}
      workspaceUuid={workspaceUuid}
      onApplied={clearIntent}
      onClose={clearIntent}
    />
  );
}
